/* eslint-disable */

class CollectionFilter {
  constructor(config, publicInterface) {
    this.initialConfig = config;
    this.publicInterface = publicInterface;

    Object.getOwnPropertyNames(Object.getPrototypeOf(this)).forEach((name) => {
      this[name] = this[name].bind(this);
    });

    this.initListener();
    this.createFlow();
    this.start();
  }

  /**
   * Init listener
   */
  initListener() {
    const listener = document.createElement('script');
    this.emit = (event) => listener.dispatchEvent(new Event(event));
    this.on = (event, func) => listener.addEventListener(event, () => func(this.publicInterface));
    this.publicInterface.emit = this.emit;
    this.publicInterface.on = this.on;
  }

  /**
   * Create flow
   */
  createFlow() {
    this.on('fetchProducts', this.renderFilters);
    this.on('renderFilters', this.processURLParams);
    this.on('processURLParams', this.setValues);
    this.on('setValues', this.filterProducts);
    this.on('filterProducts', () => {
      this.updateFilters();
      this.setPagination();
    });
    this.on('setPagination', () => {
      if (this.firstRender) {
        this.initFilters();
        this.initPagination();
      } else {
        this.writeURLParams();
      }
      this.renderProducts('total');
    });
    this.on('itemChanged', this.filterProducts);
    this.on('pageChanged', () => {
      this.writeURLParams();
      this.renderProducts('page');
    });
    this.on('reset', () => this.reset());
  }

  /**
   * Start
   */
  start() {
    const { initialConfig: config, publicInterface } = this;
    const { filters } = config;
    if (!filters?.length) return console.error('ERROR: no filters in config');
    if (filters.find((f) => filters.find((ff) => ff.name === f.name && ff !== f))) {
      return console.error('ERROR: two or more filters have identical names');
    }

    let { defaultSort } = config;
    if (defaultSort === 'manual' || defaultSort === 'best-selling') defaultSort = 'featured';

    filters.forEach((f) => {
      if (!Array.isArray(f.groupEl)) f.groupEl = [f.groupEl];
      if (!Array.isArray(f.renderFunc)) f.renderFunc = [f.renderFunc];
      if (!Array.isArray(f.removeFunc)) f.removeFunc = [f.removeFunc];
    });

    this.config = {
      shop: window.Shopify.shop,
      collection: {
        handle: config.collection?.handle || null,
        productCount: config.collection?.productCount || null,
      },
      classes: {
        group: config.classes?.group || 'js-group',
        item: config.classes?.item || 'js-item',
        itemActive: config.classes?.itemActive || 'is-active',
        itemDisabled: config.classes?.itemDisabled || 'is-disabled',
      },
      defaultSort: defaultSort || 'featured',
      render: {
        func: config.render?.func || null,
        args: config.render?.args || null,
        output: config.render?.output || null,
      },
      prerender: config.prerender || false,
      reset: {
        element: config.reset?.element || null,
      },
    };
    this.pagination = {
      type: config.pagination?.type || null,
      element: config.pagination?.element || null,
      pageSize: config.pagination?.pageSize || 12,
    };
    this.firstRender = true;
    this.filters = config.filters || [];

    publicInterface.config = this.config;
    publicInterface.pagination = this.pagination;
    publicInterface.filters = this.filters;
    publicInterface.reset = this.reset;

    return this.fetchProducts();
  }

  /**
   * Fetch products
   */
  async fetchProducts() {
    const { handle, productCount } = this.config.collection;
    if (!handle) return console.error('ERROR: no collection handle in config');
    if (!productCount) return console.error('ERROR: no collection product count in config');

    const fetches = [];
    const pages = Math.ceil(productCount / 1000);

    for (let p = 1; p <= pages; p += 1) {
      fetches.push(fetch(`/collections/${handle}?view=data&page=${p}`).then((r) => r.json()));
    }

    const products = await Promise.all(fetches).then((data) => data.flat());

    this.products = {
      initial: products,
    };

    this.publicInterface.products = this.products;

    return this.emit('fetchProducts');
  }

  /**
   * Render filters
   */
  renderFilters() {
    const { initial: products } = this.products;
    const { filters } = this;
    const { group: groupClass, item: itemClass } = this.config.classes;

    filters.forEach((f) => {
      const { name, type, groupEl, renderFunc, removeFunc, optionName } = f;
      const tags = products.map((p) => p.tags).flat();
      const omit = f.omit || [];
      const includeOnly = f.includeOnly || tags;
      const { prefix } = f;

      let getVals;

      switch (type) {
        case 'sort':
          getVals = () => {
            if (!f.vals || !f.vals.length) return console.error(`ERROR: no values for ${name}`);

            const vals = f.vals.map((v) => v);
            const featuredVals = vals.filter((v) => v.val === 'featured');

            if (this.config.defaultSort !== 'featured' && featuredVals.length) {
              featuredVals.forEach((v) => {
                vals.splice(vals.indexOf(v), 1);
              });
            }

            return vals;
          };
          break;
        case 'type':
          getVals = () => products.map((p) => p.product_type);
          break;
        case 'vendor':
          getVals = () => products.map((p) => p.vendor);
          break;
        case 'option':
          getVals = () => products.map((p) => {
            const option = p.options.find((o) => o.name.toLowerCase() === optionName.toLowerCase());
            return option ? [...option?.values.map((v) => v)] : undefined;
          }).flat();
          break;
        case 'tag':
          getVals = () => tags.filter((t) => includeOnly.indexOf(t) !== -1).filter((t) => omit.indexOf(t) === -1);
          break;
        case 'tagPrefix':
          getVals = () => [...new Set(products.map((p) => [...new Set(p.tags.filter((t) => t.slice(0, prefix.length) === prefix).map((t) => t.replace(prefix, '')))]).flat())].map((v) => ({ val: `${prefix}${v}`, title: v }));
          break;
        case 'price':
          getVals = () => {
            const prices = [...new Set(products.map((p) => parseInt(p.price, 10)))];
            return [Math.min(...prices), Math.max(...prices)];
          };
          break;
        default:
          getVals = () => [null];
      }

      const render = (vals) => {
        if (renderFunc.length !== groupEl.length) return console.error(`ERROR: found ${renderFunc.length} render function(s) for ${name}, but needed ${groupEl.length}`);

        if (type === 'price') {
          this.prices = {
            min: vals[0],
            max: vals[1],
          };

          this.publicInterface.prices = this.prices;
        }

        return groupEl.forEach((g, index) => {
          g.classList.add(groupClass);
          const isSelect = g.tagName === 'SELECT';
          if (isSelect) g.classList.add(itemClass);
          if (type === 'price') {
            g.innerHTML = renderFunc[index](vals[0], vals[1], itemClass, name);
          } else {
            g.innerHTML = vals.map((val) => renderFunc[index](val, !isSelect ? itemClass : '', name)).join('');
          }
        });
      };

      const remove = () => {
        if (removeFunc.length !== groupEl.length) return console.error(`ERROR: found ${removeFunc.length} remove function(s) for ${name}, but needed ${groupEl.length}`);
        return groupEl.forEach((g, index) => removeFunc[index](g));
      };

      let vals = [...new Set(getVals())].filter((v) => v !== undefined && v !== null && v !== '');

      if (['type', 'vendor', 'option', 'tag', 'tagPrefix'].includes(type)) {
        vals = vals.sort();
        if (f.sortOrder) {
          vals = vals.sort((a, b) => {
            if (f.sortOrder.indexOf(a) > f.sortOrder.indexOf(b)) {
              return 1;
            }
            if (f.sortOrder.indexOf(a) < f.sortOrder.indexOf(b)) {
              return -1;
            }
            return 0;
          });
        }
      }

      if (vals.length) {
        render(vals);
      } else {
        remove();
      }
    });

    return this.emit('renderFilters');
  }

  /**
   * Process URL params
   */
  processURLParams() {
    const params = new URLSearchParams(window.location.search);
    const keys = [...new Set([...params.keys()])];

    const { filters } = this;
    const queries = keys.filter((k) => filters.find((f) => f.name === k)).map((k) => (
      {
        name: k,
        vals: params.getAll(k),
      }
    ));

    const currentPage = parseInt(params.get('p'), 10) || 1;

    this.queries = queries;
    this.pagination.currentPage = currentPage;

    this.publicInterface.queries = this.queries;

    return this.emit('processURLParams');
  }

  /**
   * Set values
   */
  setValues() {
    const { filters, queries } = this;
    const { item: itemClass, itemActive: activeClass } = this.config.classes;

    filters.forEach((f) => {
      const { name, type, groupEl } = f;
      let items = [];
      groupEl.forEach((g) => {
        items = items.concat(g.classList.contains(itemClass) ? [g] : [...g.querySelectorAll(`.${itemClass}`)]);
      });

      const query = queries.find((q) => q.name === name);
      const vals = query ? query.vals : [];

      items.forEach((i) => {
        const isSelect = i.tagName === 'SELECT';
        const isActive = type === 'price' ? vals.length : vals.includes(i.dataset.value);

        if (type === 'sort' && isSelect && !vals.length) i.value = this.config.defaultSort;
        if (isSelect && vals.length) [i.value] = vals;
        if (!isSelect && isActive) i.classList.add(activeClass);
        if (type === 'price' && isActive) {
          [i.dataset.min, i.dataset.max] = vals[0].split('-');
        }
      });
    });

    return this.emit('setValues');
  }

  /**
   * Filter products
   */
  filterProducts() {
    const { filters, queries } = this;
    let products = [...this.products.initial];

    const sortProducts = (unsorted, order) => {
      let sorted;

      const sortAlphabetically = (a, b) => {
        const lowerCaseA = a.toLowerCase();
        const lowerCaseB = b.toLowerCase();

        if (lowerCaseA > lowerCaseB) return 1;
        if (lowerCaseA < lowerCaseB) return -1;
        return 0;
      };

      switch (order) {
        case 'featured':
          sorted = unsorted;
          break;
        case 'title-ascending':
          sorted = unsorted.sort(({ title: a }, { title: b }) => sortAlphabetically(a, b));
          break;
        case 'title-descending':
          sorted = unsorted.sort(({ title: a }, { title: b }) => sortAlphabetically(a, b)).reverse();
          break;
        case 'created-ascending':
          sorted = unsorted.sort(({ publishedAt: a }, { publishedAt: b }) => new Date(a).getTime() - new Date(b).getTime());
          break;
        case 'created-descending':
          sorted = unsorted.sort(({ publishedAt: a }, { publishedAt: b }) => new Date(b).getTime() - new Date(a).getTime());
          break;
        case 'price-ascending':
          sorted = unsorted.sort(({ price: a }, { price: b }) => a - b);
          break;
        case 'price-descending':
          sorted = unsorted.sort(({ price: a }, { price: b }) => b - a);
          break;
        default:
          console.error(`ERROR: invalid sort order: "${order}"`);
      }

      return sorted;
    };

    queries.forEach((q) => {
      const { name, vals } = q;
      const filter = filters.find((f) => f.name === name);
      const { type } = filter;

      let [min, max] = vals[0]?.split('-');
      min = parseInt(min, 10);
      max = parseInt(max, 10);

      switch (type) {
        case 'sort':
          products = sortProducts(products, vals[0]);
          break;
        case 'type':
          products = products.filter((p) => vals.includes(p.product_type));
          break;
        case 'vendor':
          products = products.filter((p) => vals.includes(p.vendor));
          break;
        case 'option':
          products = products.filter((p) => {
            const option = p.options.find((o) => o.name.toLowerCase() === name.toLowerCase());
            return option?.values.find((v) => vals.includes(v)) ? p : undefined;
          });
          break;
        case 'tag':
        case 'tagPrefix':
          products = products.filter((p) => p.tags.find((t) => vals.includes(t)));
          break;
        case 'price':
          products = products.filter((p) => {
            const price = parseInt(p.price, 10);
            return price >= min && price <= max;
          });
          break;
        default:
          console.error(`ERROR: invalid filter type ${type}`);
          break;
      }
    });

    this.products.filtered = products;

    return this.emit('filterProducts');
  }

  /**
   * Update filters
   */
  updateFilters() {
    const { filtered: filteredProducts } = this.products;
    const { item: itemClass, itemDisabled: itemDisabledClass } = this.config.classes;
    const filters = this.filters.filter((f) => f.type !== 'sort' && f.type !== 'price');

    filters.forEach((f) => {
      const items = f.groupEl.map((g) => [...g.querySelectorAll(`.${itemClass}`)]).flat();
      items.forEach((i) => {
        const { value } = i.dataset;
        let matchingProducts;

        switch (f.type) {
          case 'type':
            matchingProducts = filteredProducts.find((p) => p.product_type === value);
            break;
          case 'vendor':
            matchingProducts = filteredProducts.find((p) => p.vendor === value);
            break;
          case 'option':
            matchingProducts = filteredProducts.find((p) => {
              const option = p.options.find((o) => {
                const lowerCaseName = o.name.toLowerCase();
                const lowerCaseOptionName = f.optionName.toLowerCase();
                return lowerCaseName === lowerCaseOptionName;
              });
              return option && option.values.find((v) => v === value) ? p : undefined;
            });
            break;
          case 'tag':
          case 'tagPrefix':
            matchingProducts = filteredProducts.find((p) => p.tags.includes(value));
            break;
          default:
            break;
        }

        if (matchingProducts) {
          i.classList.remove(itemDisabledClass);
        } else {
          i.classList.add(itemDisabledClass);
        }
      });
    });
  }

  /**
   * Set pagination
   */
  setPagination() {
    const { filtered } = this.products;
    const { pagination } = this;

    const clone = [...filtered];
    const paginated = [];

    do {
      paginated.push(clone.splice(0, parseInt(pagination.pageSize, 10)));
    } while (clone.length);

    const currentPage = this.firstRender ? pagination.currentPage : 1;
    const viewed = Math.min(currentPage * pagination.pageSize, filtered.length);

    this.products.paginated = paginated;
    this.pagination.currentPage = currentPage;
    this.pagination.pagesTotal = paginated.length;
    this.pagination.viewed = viewed;

    return this.emit('setPagination');
  }

  /**
   * Init filters
   */
  initFilters() {
    const { filters, queries } = this;
    const { item: itemClass, itemActive: activeClass } = this.config.classes;

    const changePrice = (i) => {
      const min = parseInt(i.dataset.min, 10);
      const max = parseInt(i.dataset.max, 10);
      const { min: colMin, max: colMax } = this.prices;
      i.classList[min !== colMin || max !== colMax ? 'add' : 'remove'](activeClass);
    };

    const insertQueryVal = (name, val, isSingular, isPrice) => {
      const query = queries.find((q) => q.name === name) || { name };

      if ((isSingular || isPrice) && query.vals) query.vals = [val];
      if (!isSingular && !isPrice && query.vals) query.vals.push(val);

      if (!query.vals) {
        queries.push(query);
        query.vals = [val];
      }
    };

    const removeQueryVal = (name, val) => {
      const query = queries.find((q) => q.name === name);
      if (!query) return;

      const valIndex = query.vals.indexOf(val);
      query.vals.splice(valIndex, 1);

      if (!query.vals.length) {
        const queryIndex = queries.indexOf(query);
        queries.splice(queryIndex, 1);
      }
    };

    filters.forEach((f) => {
      const {
        name,
        type,
        groupEl,
        singular,
      } = f;

      const isPrice = type === 'price';

      const touchItem = (i, items, isSingular) => {
        if (!isPrice) {
          if (isSingular) {
            items.forEach((item) => {
              item.classList[item === i ? 'add' : 'remove'](activeClass);
            });
          } else {
            i.classList.toggle(activeClass);
          }
        }

        if (isPrice) changePrice(i);
      };

      groupEl.forEach((gx, gxIndex) => {
        const items = gx.classList.contains(itemClass) ? [gx] : [...gx.querySelectorAll(`.${itemClass}`)];

        items.forEach((i, iIndex) => {
          const isSelect = i.tagName === 'SELECT';
          const isSingular = isSelect || singular;

          i.addEventListener(isSelect || isPrice ? 'change' : 'click', () => {
            let val;
            if (isSelect) val = i.value;
            if (!isSelect && !isPrice) val = i.dataset.value;
            if (isPrice) val = `${i.dataset.min}-${i.dataset.max}`;

            if (!isSelect) touchItem(i, items, isSingular);

            if (groupEl.length > 1) {
              groupEl.filter((gy, gyIndex) => gyIndex !== gxIndex).forEach((gy) => {
                const gyItems = gy.classList.contains(itemClass) ? [gy] : [...gy.querySelectorAll(`.${itemClass}`)];
                if (!isSelect) touchItem(gyItems[iIndex], gyItems, isSingular);
                if (isSelect) gyItems[iIndex].value = val;
              });
            }

            const isActive = isSelect || i.classList.contains(activeClass);

            if (isActive) {
              insertQueryVal(name, val, isSingular, isPrice);
            } else {
              removeQueryVal(name, val);
            }

            this.emit('itemChanged');
          });
        });
      });
    });

    this.config.reset.element?.addEventListener('click', () => this.emit('reset'));

    return this.emit('initFilters');
  }

  /**
   * Init pagination
   */
  initPagination() {
    const { type, element, pageSize } = this.pagination;

    if (!type) return console.error('ERROR: no pagination type in config');
    if (!element) return console.error('ERROR: no pagination element in config');

    const next = () => {
      const { pagination } = this;
      const { pagesTotal } = pagination;
      let { currentPage } = pagination;

      if (currentPage < pagesTotal) {
        currentPage += 1;
        const viewed = Math.min(currentPage * pageSize, this.products.filtered.length);

        this.pagination.currentPage = currentPage;
        this.pagination.viewed = viewed;

        this.emit('pageChanged');
      } else if (type === 'button') {
        console.warn('WARNING: no more pages, you might want to hide the pagination button');
      }
    };

    if (type === 'button') {
      element.addEventListener('click', next);
    } else if (type === 'scroll') {
      const observer = new IntersectionObserver(next, {});
      observer.observe(element);
    }

    return this.emit('initPagination');
  }

  /**
   * Write URL params
   */
  writeURLParams() {
    const { queries } = this;
    const pairs = queries.map((q) => q.vals.map((v) => [q.name, v])).flat();

    const params = new URLSearchParams(pairs);

    const { currentPage } = this.pagination;
    if (currentPage > 1) params.set('p', currentPage);

    const string = params.toString();

    window.history.replaceState({}, '', `${window.location.pathname}${string.length ? '?' : ''}${string}`);

    this.emit('writeURLParams');
  }

  /**
   * Render products
   */
  renderProducts(mode) {
    const { products } = this;
    const { currentPage } = this.pagination;
    const { output, func, args } = this.config.render;

    if (!output) return console.error('ERROR: no output element in config');
    if (!func) return console.error('ERROR: no render function in config');

    if (mode === 'total') {
      const performRender = !this.config.prerender || !this.firstRender || this.queries.length || this.pagination.currentPage !== 1;

      if (performRender) {
        output.innerHTML = products.paginated.slice(0, currentPage).flat().map((p) => func({ product: p, ...args })).join('');
        this.firstRender = false;
      }
    } else if (mode === 'page') {
      products.paginated[currentPage - 1].forEach((p) => {
        const div = document.createElement('div');
        output.appendChild(div);
        div.outerHTML = func({ product: p, ...args });
      });
    }

    if (this.firstRender) this.firstRender = false;

    return this.emit('renderProducts');
  }

  /**
   * Reset
   */
  reset(filter = undefined) { // resets either a single filter or all filters at once
    const { item: itemClass, itemActive: activeClass } = this.config.classes;
    const filters = filter ? [filter] : this.filters;

    filters.forEach((f) => {
      const { groupEl } = f;
      let items = [];
      groupEl.forEach((g) => {
        items = items.concat(g.classList.contains(itemClass) ? [g] : [...g.querySelectorAll(`.${itemClass}`)]);
      });
      items.forEach((i) => {
        const isSelect = i.tagName === 'SELECT';
        if (!isSelect) i.classList.remove(activeClass);
        if (isSelect) {
          if (f.type === 'sort' && [...i.options].find((o) => o.value === this.config.defaultSort)) {
            i.value = this.config.defaultSort;
          } else {
            i.value = i.options[0].value;
          }
        }
        if (f.type === 'price') {
          i.dataset.min = this.prices.min;
          i.dataset.max = this.prices.max;
        }
      });
    });

    this.queries.splice(0, this.queries.length);

    return this.emit('itemChanged');
  }
}

export default (config) => {
  const publicInterface = {};
  const collectionFilter = new CollectionFilter(config, publicInterface);
  return publicInterface;
};

/* eslint-enable */
